import React, { useEffect, useState } from "react";
import { Loader, X } from "lucide-react";
import { Button } from "@/subframe/components/Button";
import { IconButton } from "@/subframe/components/IconButton";

// Define the global window interface to include deferredPrompt
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deferredPrompt: any;
  }
}

// Store the prompt event internally as a fallback
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let deferredPromptLocal: any = null;

const DirectInstallPWA: React.FC = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installStatus, setInstallStatus] = useState<
    "idle" | "loading" | "success" | "error" | "info"
  >("idle");
  const [animation, setAnimation] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Capture and store install prompt
  useEffect(() => {
    // Check if the app is already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return; // Exit early if already installed
    }
    
    // First check if we already have a prompt stored in the window object
    if (window.deferredPrompt) {
      deferredPromptLocal = window.deferredPrompt;
      setIsInstallable(true);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use - both locally and globally
      deferredPromptLocal = e;
      window.deferredPrompt = e;
      
      // Update UI to show install button
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Detect when app gets installed during the session
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsVisible(false);
    });

    // Trigger entrance animation
    setTimeout(() => {
      setAnimation(true);
    }, 300);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', () => {});
    };
  }, []);

  const handleInstallClick = async () => {
    setIsLoading(true);
    setInstallStatus("loading");
    setMessage("Install our app for a better experience!");

    // Try to use prompt from window first, then fall back to local variable
    const promptToUse = window.deferredPrompt || deferredPromptLocal;

    if (!promptToUse) {
      setMessage(
        "Installation not available. Please try using Chrome or Edge on desktop."
      );
      setInstallStatus("error");
      setIsLoading(false);
      return;
    }

    try {
      // Show the install prompt
      promptToUse.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await promptToUse.userChoice;

      if (outcome === "accepted") {
        setMessage("Successfully installed! Launching app...");
        setInstallStatus("success");
        setIsInstallable(false);
        
        // Set installed and completely hide after short delay
        setIsInstalled(true);
        setTimeout(() => {
          setIsVisible(false);
        }, 2000);
      } else {
        setMessage("Installation cancelled.");
        setInstallStatus("info");
      }

      // Clear the saved prompts - they can't be used again
      window.deferredPrompt = null;
      deferredPromptLocal = null;
          } catch (error) {
      setMessage("Installation failed. Please try again.");
      setInstallStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Attempt to trigger install from Safari on iOS
  const handleIOSInstall = () => {
    setMessage("Add to Home Screen via the share menu to install");
    setInstallStatus("info");
  };

  const dismissMessage = () => {
    setMessage("");
  };

  // Force show installation button for testing - but honor the isInstalled state
  const forceShowButton = true && !isInstalled; 

  // If already installed or visibility disabled, return nothing
  if (isInstalled || !isVisible || (!isInstallable && !forceShowButton)) {
    return null;
  }

  const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());

  return (
    <>
      <div
        className={`transition-all duration-500 ${
          animation ? "opacity-100" : "opacity-0"
        }`}
      >
        <IconButton
          variant="brand-tertiary"
          size="large"
          icon="FeatherDownload"
          onClick={() => setIsDrawerOpen(true)}
        />
      </div>

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
          isDrawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer Content */}
        <div className="p-6 space-y-6 overflow-y-auto h-[calc(100%-80px)]">
          {message && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl p-4">
              <div className="flex-1 text-sm text-blue-700">{message}</div>
              <button
                onClick={dismissMessage}
                className="text-blue-400 hover:text-blue-600 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          )}

          <div className="space-y-6">
            {/* App Preview */}
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
              <div className="relative p-5">
                <div className="flex items-center gap-3">
                  <img 
                    src="/android-chrome-192x192.png" 
                    alt="Notifyer Logo" 
                    className="w-12 h-12 rounded-xl shadow-lg"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-white">Notifyer</h3>
                    <p className="text-xs text-white/80">Stay connected with your messages</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Download Options */}
            <div className="bg-white rounded-2xl p-5 space-y-5 border border-slate-200 shadow-sm">
              {/* PWA */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Progressive Web App</h3>
                  <p className="text-xs text-gray-500">Install directly in your browser</p>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      isIOS ? handleIOSInstall() : handleInstallClick();
                    }}
                    className="flex-1"
                  >
                    <img
                      src="/android-chrome-512x512.png"
                      alt="Install PWA"
                      className="h-10 w-auto rounded-lg"
                    />
                  </a>
                  <Button
                    className="bg-white hover:bg-slate-50 text-gray-800 border border-slate-200 transition-all duration-200 h-10 px-4 rounded-lg font-medium shadow-sm hover:shadow text-sm"
                    variant="neutral-secondary"
                    onClick={isIOS ? handleIOSInstall : handleInstallClick}
                    disabled={isLoading && installStatus === "loading"}
                  >
                    <div className="flex items-center gap-2">
                      <span>Install PWA</span>
                      {installStatus === "loading" && (
                        <Loader className="animate-spin" size={14} />
                      )}
                    </div>
                  </Button>
                </div>
              </div>

              {/* Mobile Apps */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Mobile Apps</h3>
                  <p className="text-xs text-gray-500">Download for iOS or Android</p>
                </div>
                <div className="flex flex-row items-center gap-x-2">
                  <a
                    href="https://apps.apple.com/es/app/notifyer-by-whatsable/id6743722183?l=en-GB"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block transform hover:scale-[1.02] transition-transform duration-200 m-0 p-0"
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      className="h-10 w-auto m-0 p-0"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.chatmobile.chat_mobile"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block transform hover:scale-[1.02] transition-transform duration-200 m-0 p-0"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt="Get it on Google Play"
                      className="h-10 w-auto m-0 p-0"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  );
};

export default DirectInstallPWA;